<template>
  <div
    class="prosemirror-input"
    ref="editor"
    :class="{ focussed: focussed, 'not-editable': !editable }"
    @click="focus()"
  >
    <div class="prosemirror-input__placeholder" v-if="placeholder && !content.length">
      {{ placeholder }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { EditorState, TextSelection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Node } from 'prosemirror-model';

import plainSchema from '@prosemirror/schema/plainSchema';
import { contentFromNode, nodeFromContent } from '@prosemirror/schema/plainAndLinebreakUtil';
import { keydownHandler, keymap } from 'prosemirror-keymap';

import {
  createMaxContentSizePlugin /** pluginKey as maxContentSizePlugin **/,
} from '@/prosemirror/plugins/maxContentSize';

import { config } from '@common/config';

export default defineComponent({
  name: 'ProsemirrorInput',

  props: {
    blurOnEnter: {
      type: Boolean,
      required: false,
      default: false,
    },

    focusOnMount: {
      type: Boolean,
      required: false,
      default: false,
    },

    modelValue: {
      type: String,
      required: true,
    },

    placeholder: {
      type: String,
      required: false,
      default: '',
    },

    editable: {
      type: Boolean,
      required: false,
      default: true,
    },

    keymap: {
      type: Object,
      required: false,
      default: null,
    },

    schema: {
      type: Object,
      required: false,
      default: () => plainSchema,
    },

    maxLength: {
      type: Number,
      required: false,
      default: config.entityNameMaxlength,
    },
  },
  emits: { 'update:modelValue': null, change: null, blur: null, focus: null },

  data(): {
    /**
     * Must be non-reactive
     */
    view?: EditorView;
    content: string;
    focussed: boolean;
    initialValue: string;
  } {
    return {
      content: '',
      focussed: false,
      initialValue: '',
    };
  },

  methods: {
    handleDispatch(transaction) {
      const newState = this.view.state.apply(transaction);

      this.view.updateState(newState);

      if (
        transaction.docChanged &&
        (!transaction.meta.prosemirrorInput || !transaction.meta.prosemirrorInput.replaceContent)
      ) {
        this.content = contentFromNode(this.view.state.doc);
        this.$emit('update:modelValue', this.content);
      }
    },
    /**
     * Create Editor State.
     *
     * @returns {EditorState} Editor state.
     */
    createEditorState(content) {
      const plugins = [];

      if (Number.isInteger(this.maxLength)) {
        plugins.push(createMaxContentSizePlugin(this.maxLength));
      }

      if (this.keymap) {
        plugins.push(keymap(this.keymap));
      }

      const state = EditorState.create({
        doc: Node.fromJSON(this.schema, content || JSON.parse('{"type":"doc"}')),
        plugins,
      });

      // move the selection to the end so when the input is focussed the cursor is at the end
      // instead of the start
      const tr = state.tr;

      const lastPosition = state.doc.content.size;
      const selection = TextSelection.create(tr.doc, lastPosition);

      return state.apply(tr.setSelection(selection));
    },

    /**
     * Create Editor View.
     *
     * @returns {EditorView} Editor view.
     */
    createEditorView(content) {
      const viewConfig = {
        state: this.createEditorState(content),
        editable: () => this.editable,
        dispatchTransaction: this.handleDispatch,
        handleDOMEvents: {
          blur: () => {
            this.$emit('blur');
            this.focussed = false;

            // only emit change event if content has changed.
            // initialValue holds the value from the last change event.
            // eslint-disable-next-line eqeqeq
            if (this.content != this.initialValue) {
              this.initialValue = this.content;
              this.$emit('change', this.content);
            }
          },

          focus: () => {
            this.$emit('focus');
            this.focussed = true;
          },
        },
        // This can be used later to set state in the component based on the size state
        // dispatchTransaction: transaction => {
        //   const newState = this.view.state.apply(transaction);
        //   this.view.updateState(newState);

        //   const plugin = maxContentSizePlugin.get(newState);

        //   if (plugin) {
        //     const overMax = plugin.getState(newState).overMax;
        //   }
        // },
      };

      if (this.blurOnEnter) {
        viewConfig.handleKeyDown = keydownHandler({
          Enter: (state, dispatch, editorView) => {
            editorView.dom.blur();

            return true;
          },
        });
      }

      return new EditorView(this.$refs.editor, viewConfig);
    },

    /**
     * Replaces the content of the editor.
     *
     * @param {String} content
     */
    replaceContent(content) {
      const node = Node.fromJSON(this.schema, nodeFromContent(content));

      const transaction = this.view.state.tr;

      transaction.replaceWith(0, this.view.state.doc.content.size, node);
      transaction.setMeta('prosemirrorInput', { replaceContent: true });
      this.view.dispatch(transaction);
    },

    /**
     * Focus the input.
     */
    focus() {
      this.view.focus();
    },
  },

  created() {
    this.content = this.modelValue;
    this.initialValue = this.modelValue;
  },

  /**
   * Mounted hook.
   */
  mounted() {
    this.view = this.createEditorView(nodeFromContent(this.modelValue));

    if (this.focusOnMount) {
      this.focus();
    }
  },

  beforeUnmount() {
    // Make sure the ProseMirror DOM is removed
    // use setTimeout to avoid hiding the input before the parent (looking weird on modals for example)
    setTimeout(() => this.view.destroy(), 200);
  },

  watch: {
    editable() {
      this.view.update(this.view.props);
    },

    modelValue(val) {
      // set the initialValue always, so that when the input is focussed and blurred,
      // the change event isn't fired if visual and initialValue are the same
      this.initialValue = val;

      if (!this.focussed) {
        this.content = val;

        this.replaceContent(val);
      }
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';

.prosemirror-input {
  position: relative;
  overflow: hidden;
  cursor: text;
  color: colors.$color-black;

  @include typography.typography-14-normal(24px);

  user-select: initial;
}

.prosemirror-input__placeholder {
  position: absolute;
  color: colors.$color-grey-400;
  pointer-events: none;
  height: 100%;
}

// mimic input box
.prosemirror-input--boxed {
  border-radius: settings.$border-radius;
  box-shadow: settings.$box-shadow-border-grey-50;
  background: colors.$color-grey-100;
  color: colors.$color-grey-800;
  transition: all 0.1s ease-in-out;

  @include typography.typography-14-normal(160%);

  .ProseMirror {
    padding: 12px 12px 16px;
    /* min-height: 48px; */
  }

  .prosemirror-input__placeholder {
    padding: 12px 12px 16px;
    color: colors.$color-placeholder;
  }

  &:hover {
    background: colors.$color-white;
  }

  &.focussed {
    background: colors.$color-white;
    border: none;
    box-shadow: settings.$box-shadow-focus-ring;
    box-shadow: settings.$box-shadow-focus-ring, 0 0 0 1px colors.$color-blue-300;
    caret-color: colors.$color-blue-600;
  }

  &.not-editable {
    cursor: default;
    pointer-events: none;
    color: colors.$color-grey-400;

    &:hover {
      background: colors.$color-grey-100;
    }
  }
}
</style>
